import { useAuthStore } from "./auth.js";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4000";
export const apiBaseUrl = BASE;

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface Options extends RequestInit {
  json?: unknown;
}

interface CuerpoError {
  error?: { code?: string; message?: string; details?: unknown };
}

function cabeceras(opts: Options): Headers {
  const headers = new Headers(opts.headers);
  headers.set("Accept", "application/json");
  const token = useAuthStore.getState().accessToken;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
    opts.body = JSON.stringify(opts.json);
  }
  return headers;
}

/**
 * Con 401, intenta refrescar el token una vez y repetir el pedido. La sesión se cierra sólo si el refresco falla: si el
 * reintento devuelve otro error (un 409 al crear, por ejemplo), es el error del pedido y lo ve quien llamó, sin perder la página.
 */
async function reintentarConRefresh(path: string, opts: Options, headers: Headers): Promise<Response | null> {
  const refreshed = await useAuthStore.getState().tryRefresh();
  if (!refreshed) {
    useAuthStore.getState().logout();
    return null;
  }
  const retryHeaders = new Headers(headers);
  retryHeaders.set("Authorization", `Bearer ${useAuthStore.getState().accessToken ?? ""}`);
  return fetch(`${BASE}${path}`, { ...opts, headers: retryHeaders });
}

async function cuerpoDe<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  return (await res.json().catch(() => ({}))) as T;
}

async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const headers = cabeceras(opts);
  let res = await fetch(`${BASE}${path}`, { ...opts, headers });
  // Si no se pudo refrescar queda el 401 original; si se reintentó, vale lo que respondió el reintento.
  if (res.status === 401) res = (await reintentarConRefresh(path, opts, headers)) ?? res;
  if (res.ok) return cuerpoDe<T>(res);

  const body = (await res.json().catch(() => ({}))) as CuerpoError;
  const err = body?.error ?? {};
  throw new ApiError(res.status, err.code ?? "ERROR", err.message ?? res.statusText, err.details);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) => request<T>(path, { method: "POST", json }),
  patch: <T>(path: string, json?: unknown) => request<T>(path, { method: "PATCH", json }),
  put: <T>(path: string, json?: unknown) => request<T>(path, { method: "PUT", json }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /** PUT con el cuerpo en binario (por ejemplo, una imagen con su Content-Type). */
  putBinary: <T>(path: string, cuerpo: Blob) =>
    request<T>(path, { method: "PUT", body: cuerpo, headers: { "Content-Type": cuerpo.type || "application/octet-stream" } }),
};
